<template>
    <div class="mt-3">
        <v-row class="px-3">
            <v-col cols="12" align="center" v-if="programs.length === 0">
                <template v-if="loading">
                    <loading-spinner no-margin-top />
                    <small>{{ $t('common.loading') }}...</small>
                </template>
                <small v-else>{{ $t('programs.no_programs_founded') }}</small>
            </v-col>

            <v-col cols="12" v-for="program in programs" :key="program.objectID" class="pa-4">
                <program-card @click="goToProgram(program.objectID)" :program="program" with-menu
                    @delete="showDeleteProgram(program.objectID)" @assign="showAssignProgramDialog(program)"
                    @copy="onCopyProgram(program)" @folder="showSaveFolder(program)" />
            </v-col>
            <v-col cols="12" v-if="pageCount > 1">
                <kimsa-pagination @page="page = $event - 1" :pageCount="pageCount" no-right
                    :total-visible="isMobile ? 4 : (totalVisiblePagination || 7)" />
            </v-col>
        </v-row>

        <save-in-folder-dialog ref="saveInFolder" :current-id="programId" :default-folder="folderId"
            @cancel="programId = null; folderId = null" type="programs" />

        <assign-program-dialog ref="assign" @hide="programId = null" :program-id="programId" :coach-id="coachId" />

        <kimsa-confirm ref="showDelete" :title="$t('programs.delete_title')" :content="$t('programs.confirm_deleted')"
            :persistent="false" :loading="loadingDelete" @confirm="deleteProgram" @cancel="programId = null"
            :close-on-confirm="false" :alert-mode="true" />
    </div>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { Client, ProgramIndex, UserIndex } from "@/utils/Algolia";
import { db, Timestamp } from "@/utils/Firebase";
import { debounce } from "@/utils/Common";
import ProgramCard from "@/components/common/routines/ProgramCard";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { mapMutations } from "vuex";
import SaveInFolderDialog from "@/components/coach/SaveInFolderDialog";
import AssignProgramDialog from './AssignProgramDialog.vue';
import KimsaPagination from "@/components/common/KimsaPagination";

export default {
    name: "Programs",
    components: { KimsaPagination, SaveInFolderDialog, LoadingSpinner, ProgramCard, KimsaConfirm, KimsaTextField, AssignProgramDialog },
    props: {
        search: {
            type: String,
            default: '',
        },
        folder: {
            type: String,
            default: '',
        },
        totalVisiblePagination: {
            type: [Number, String],
            default: null,
        }
    },
    data() {
        return {
            loading: false,
            loadingDelete: false,
            programId: null,
            programs: [],
            coaches: [],
            folderId: null,
            page: 0,
            pageCount: 0,
            perPage: 6,
        }
    },
    computed: {
        isCompany(vm = this) {
            return vm.account?.accountType === 'company'
        },
        coachId(vm = this) {
            return vm.currentUser.uid
        }
    },
    async mounted() {
        let vm = this
        vm.isCompany ? await vm.loadCoaches() : await vm.loadPrograms()
    },
    methods: {
        ...mapMutations(['showOverlay', 'hideOverlay']),
        async loadCoaches() {
            const vm = this
            let filter = `coachSubscription.companyId: ${vm.coachId} AND coachSubscription.paused != 1`
            await Client.clearCache()
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 10000,
            })
                .then(({ hits }) => {
                    vm.coaches = [vm.coachId, ...hits.map(hit => (hit.objectID))]
                })
                .catch(err => {
                    console.log(err);
                });
        },
        goToProgram(id) {
            const vm = this
            let query = {}
            if (vm.$route.query?.studentId) query.studentId = vm.$route.query.studentId
            vm.$router.push({ name: `${vm.accountType}.routines.program.detail`, params: { uid: id }, query: query })
        },
        async showDeleteProgram(id) {
            const vm = this
            vm.programId = id
            vm.kimsaConfirmContent = vm.$t('routines.confirm_deleted_routine')
            vm.$refs.showDelete.show()
        },
        async deleteProgram() {
            const vm = this
            vm.loadingDelete = true
            await db.collection('programs').doc(vm.programId).delete()
            vm.programs = vm.programs.filter(routine => routine.objectID !== vm.programId)
            vm.toast(vm.$t('programs.deleted'))
            vm.programId = null
            vm.$refs.showDelete.hide()
            vm.loadingDelete = false
        },
        debouncedLoadPrograms: debounce(
            function () {
                this.loadPrograms()
            }, 1000),
        async loadPrograms() {
            const vm = this
            vm.loading = true
            vm.programs = []
            let coaches = vm.coaches.length === 0 ? vm.coachId : vm.coaches.join(' OR coachId:')
            let filter = `coachId: ${coaches}`
            if (vm.folder) filter += ` AND folder: ${vm.folder}`
            await Client.clearCache()
            let response = await ProgramIndex.search(vm.search, {
                filters: filter,
                hitsPerPage: vm.perPage,
                page: vm.page,
            })
            vm.pageCount = response.nbPages || 0
            vm.programs = response.hits
            vm.loading = false
        },
        showAssignProgramDialog(program) {
            const vm = this;
            vm.programId = program.objectID
            vm.$refs.assign.show()
        },
        async onCopyProgram(program) {
            const vm = this
            vm.showOverlay(true)
            // console.log('program', program)

            let payload = {
                ...program,
                coachId: vm.currentUser.uid,
                name: `${program.name} ${vm.$t('common.copy_template')}`
            }
            delete payload.created_at
            delete payload.objectID
            payload = JSON.parse(JSON.stringify(payload))
            await db.collection('programs').add({ ...payload, created_at: Timestamp.now() })

            vm.hideOverlay()
            vm.toast(vm.$t('programs.copied'))
            vm.refresh()
        },
        showSaveFolder(program) {
            const vm = this
            vm.programId = program.objectID
            vm.folderId = program.folder || null
            vm.$refs.saveInFolder.show()
        },
        refresh() {
            const vm = this
            vm.loadPrograms()
        },
    },
    watch: {
        coaches() {
            this.loadPrograms()
        },
        search(val) {
            this.programs = []
            if (!val) return this.loadPrograms()
            return this.debouncedLoadPrograms()
        },
        loading(val) {
            this.$emit('loading', val)
        },
        page() {
            this.loadPrograms()
        },
        folder() {
            this.programs = []
            this.refresh()
        }
    }
}
</script>

<style scoped></style>